import { useForm } from "react-hook-form";
import { useState, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { submitFNOL } from "service/FNOL/submitFNOL";
import {
  FACFormDataTextFormat,
  phoneNumberWithUSACodeFormatting,
} from "js/formatting";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import DialogBackdrop from "components/ModalDialogs/DialogBackdrop";
import GeneralPurposeDialog from "components/General/GeneralPurposeDialog";
import config from "config/config";
import moment from "moment/moment";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";

export default function FACStepSix({ formStep, setFormStep, FACFormData }) {
  const { register: FACStepSixRegister, handleSubmit: FACStepSixHandleSubmit } =
    useForm();
  const { csrUser } = useSelector((s) => s.csrUser);
  const { user } = useSelector((s) => s.user);

  const MAXIMUMWORDCOUNT = 300;
  const [currentWordCount, setCurrentWordCount] = useState(0);

  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [confirmation, setConfirmation] = useState();
  const [openDialog, setOpenDialog] = useState(false);
  const [message, setMessage] = useState();
  const [title, setTitle] = useState();
  const { t } = useTranslation();

  const onError = () => {
    setIsLoading(false);
    setOpenDialog(true);
    setTitle(t("AgentFACStepSix-errorTitle"));
    setMessage(t("AgentFACStepSix-errorMessage"));
  };

  async function getFACFormDataString(data) {
    return new Promise((res, rej) => {
      res(FACFormDataTextFormat(data));
    });
  }
  const getMailAddress = () => {
    if (FACFormData?.claimsPacketDeliveryMethod == "fedex")
      return `${FACFormData?.mailingAddress}  ${FACFormData?.mailingAddress2} ${FACFormData?.mailingCity} ${FACFormData?.mailingState} ${FACFormData?.mailingZip}`;
    else if (FACFormData?.claimsPacketDeliveryMethod == "email")
      return FACFormData?.mailingEmail;
    else if (FACFormData?.claimsPacketDeliveryMethod == "fax")
      return FACFormData?.mailingFax;
  };

  function getFACObjectMapper() {
    return {
      fnolReceivedFrom: "Agency Portal",
      fnolId: null,
      informantsName: `${FACFormData?.firstNameClaimer} ${FACFormData?.lastNameClaimer}`,
      companyName: "",
      mailsFromToAddress: getMailAddress() ?? "",
      mailFormsToName:
        FACFormData?.claimsPacketDeliveryMethod == "fedex"
          ? `${FACFormData?.mailingFirstName} ${FACFormData?.mailingLastName}`
          : "",
      mailFormsToCompanyName: FACFormData?.mailingCompanyName ?? "",
      informantPhoneNumber: FACFormData?.phoneClaimer
        ? phoneNumberWithUSACodeFormatting(FACFormData?.phoneClaimer)
        : "",
      informantAddress1: FACFormData?.streetAddressClaimer ?? "",
      informantAddress2: FACFormData?.streetAddress2Claimer ?? "",
      informantCity: FACFormData?.cityClaimer ?? "",
      informantState: FACFormData?.stateClaimer ?? "",
      informantZip: FACFormData?.zipClaimer ?? "",
      formsRequestedBy: "",
      informantCountry: "",
      informantFaxNumber: "",
      informantEmail: FACFormData?.emailClaimer ?? "",
      relationshipDeceased: "",
      deceasedFullName: `${FACFormData?.firstName} ${FACFormData?.lastName}`,
      deceasedFirstName: FACFormData?.firstName ?? "",
      deceasedLastName: FACFormData?.lastName ?? "",
      deceasedMiddleName: FACFormData?.middleName ?? "",
      deceasedId: FACFormData?.ssn ?? "",
      deceasedDateOfBirth: FACFormData?.dob
        ? moment(FACFormData?.dob).format("MM/DD/YYYY")
        : "",
      deceasedDateOfDeath: FACFormData?.dod
        ? moment(FACFormData?.dod).format("MM/DD/YYYY")
        : "",
      mannerOfDeath: FACFormData?.manner ?? "",
      foreignDeath: FACFormData?.foreign === "no" ? "false" : "true",
      whichCountry: FACFormData?.foreignCountry ?? "",
      fnolType: "D",
      fnolReceivedDate: moment().format("MM/DD/YYYY"),
      funeralHomeInvolved: FACFormData?.funeralHomeName ? "true" : "false",
      nameFuneralHome: FACFormData?.funeralHomeName ?? "",
      specialInstructions: FACFormData?.mailingComment ?? "",
      funeralPhoneNumber: FACFormData?.funeralHomePhoneNumber
        ? phoneNumberWithUSACodeFormatting(FACFormData?.funeralHomePhoneNumber)
        : "",
      funeralEmployeeName: "",
      veifiedByGoogle: "",
      funeralAssignment: "",
      amountAssignment: 0,
      deathCertificateRequest: "false",
      causeOfDeath: FACFormData?.manner ?? "",
      stateOfResidence: "",
      maritalStatus: FACFormData?.marital ?? "",
      nameOfSpouse:
        FACFormData?.marital === "married"
          ? FACFormData?.spouseName
          : FACFormData?.marital === "widowed"
          ? FACFormData?.deceasedSpouse
          : "",
      verifiersName: "",
      callerAgentOnPolicy: "false",
      personalDeliveryBeneficiary: "false",
      personalDeliveryCustomer: "false",
      agentDeliveryPreference: FACFormData?.claimsPacketDeliveryMethod ?? "",
      policyIssuedCountry: "US",
      region: "US",
      dateOfVerification: "",
      fnolCorrelationId: moment().format("YYYY-MM-DD HH:MM:SS"),
      totalPolicyCount: 1,
      deathCertificateStatus: "",
    };
  }

  const onSubmit = async (data) => {
    let submitData = {
      ...FACFormData,
      ...data,
    };
    if (!csrUser?.userId) {
      setIsLoading(true);
      let formattedFACFormData = await getFACFormDataString(submitData);
      const fnolRequest = getFACObjectMapper();
      await submitFNOL(
        submitData?.policyNumber,
        formattedFACFormData,
        user.userId,
        fnolRequest
      )
        .then((response) => {
          console.log(response);
          setIsLoading(false);
          setFormStep(7);
          setConfirmation(response?.transactionId);
          setIsConfirmationDialogOpen(true);
          setOpenDialog(false);
        })
        .catch((error) => {
          console.error(error);
          onError();
        });
    } else {
      setOpenDialog(true);
      setTitle(t("AgentFACStepSix-dialogTitle"));
      setMessage(t("AgentFACStepSix-dialogMessage"));
      setFormStep(6);
    }
  };

  return (
    <>
      {formStep === 6 && (
        <EverlakeLoadingOverlay
          isLoading={isLoading}
          loadingText={t("AgentFACStepSix-loadingText")}
        >
          <div>
            <p className="text-sm">{t("AgentFACStepSix-heading")}</p>
            <hr />

            <form onSubmit={FACStepSixHandleSubmit(onSubmit)}>
              <div className="grid gap-4 grid-cols-1 lg:gap-6">
                <div>
                  <label id="ID_messageLabel" htmlFor="ID_message">
                    {t("AgentFACStepSix-messageLabel", {
                      wordCount: MAXIMUMWORDCOUNT,
                    })}
                  </label>
                  <textarea
                    id="ID_message"
                    type="text"
                    name="message"
                    aria-labelledby="ID_messageLabel"
                    aria-describedby="ID_messageError"
                    className="mt-1"
                    rows="6"
                    {...FACStepSixRegister("notes", {
                      maxLength: {
                        value: MAXIMUMWORDCOUNT,
                        message: t("AgentFACStepSix-maxLengthMessage"),
                      },
                      onChange: (e) => {
                        setCurrentWordCount(e.target.value.length);
                      },
                    })}
                  />
                  <div
                    className={
                      currentWordCount > MAXIMUMWORDCOUNT
                        ? "text-red-600 text-sm text-right"
                        : "text-sm text-right"
                    }
                  >
                    ({currentWordCount}/{MAXIMUMWORDCOUNT})
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    className="ui-btn-secondary ui-btn-min-w mt-4"
                    type="button"
                    onClick={() => setFormStep((current) => current - 1)}
                  >
                    {t("AgentFACStepSix-previousButtonLabel")}
                  </button>

                  <button className="ui-btn-primary ui-btn-min-w mt-4">
                    {t("AgentFACStepSix-submitButtonLabel")}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </EverlakeLoadingOverlay>
      )}

      {/* confirmation modal */}
      <Transition appear show={isConfirmationDialogOpen} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-10 overflow-y-auto"
          onClose={() => null}
        >
          <DialogBackdrop />
          <div className="min-h-screen px-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block w-full max-w-2xl p-6 my-32 overflow-hidden text-center align-middle transition-all transform bg-white shadow-xl rounded-lg">
                <Dialog.Title
                  as="h2"
                  className="mx-auto pt-20 pb-8 dialog_label2 font-body text-4xl font-extrabold tracking-tighter text-center"
                >
                  {t("AgentFACStepSix-confirmationDialogTitle")}
                </Dialog.Title>

                <div className="mx-auto lg:w-4/5">
                  <p id="ID_dialogPaymentThankDesc">
                    <strong>
                      {t("AgentFACStepSix-confirmationDialogSubHeading")}
                    </strong>
                  </p>
                  <div className="w-full text-2xl font-bold tracking-tight text-theme-action my-2 text-center xl:text-3xl xl:my-4">
                    {confirmation}
                  </div>
                  <hr />
                  <div className="w-full text-lg font-bold tracking-tight text-black my-2 text-center xl:text-xl xl:my-4">
                    {t("AgentFACStepSix-confirmationDialogSubHeading1")}
                  </div>
                  <div className="text-left font-normal tracking-normal px-4">
                    <div className="w-full text-lg font-bold tracking-tight mt-2 text-black my-2 xl:text-xl xl:mb-2 xl:mt-8">
                      {t("AgentFACStepSix-confirmationDialogOption1")}
                    </div>
                    <p className="text-lg font-semibold max-w-sm">
                      {t("AgentFACStepSix-confirmationDialogOption2", {
                        serviceEmail: config.AGENT_SERVICE_EMAIL,
                      })}
                    </p>
                    <p className="text-lg font-semibold max-w-sm">
                      {t("AgentFACStepSix-confirmationDialogOption3")}
                    </p>
                    <p className="text-lg font-semibold max-w-sm">
                      {t("AgentFACStepSix-confirmationDialogOption4")}
                    </p>
                    <p className="text-lg font-semibold max-w-sm">
                      {t("AgentFACStepSix-confirmationDialogOption5")}
                    </p>
                    <div className="w-full text-lg font-bold tracking-tight mt-2 text-black my-2 xl:text-xl xl:mb-2 xl:mt-8">
                      {t("AgentFACStepSix-confirmationDialogOption6")}
                    </div>
                    <p className="text-lg font-semibold max-w-md">
                      {t("AgentFACStepSix-footer")}
                    </p>
                  </div>
                </div>
                <div className="block text-center mt-12 mb-8 ">
                  <Link
                    className="ui-btn-primary min-w-[140px] text-base font-semibold mt-4 py-4 px-8"
                    aria-label="Go back to main page"
                    to="/dashboard"
                  >
                    {t("AgentFACStepSix-mainPageLinkLabel")}
                  </Link>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>

      <GeneralPurposeDialog
        showCloseButton={false}
        dialogOnClose={() => null}
        state={openDialog}
        setState={setOpenDialog}
        title={title}
        desc={message}
        secondaryButtonAction={() => {
          setOpenDialog(false);
          setFormStep(7);
        }}
      />
    </>
  );
}
